import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-premium';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import ReadOnlyContact from '@/components/common/contact_components/ReadOnlyContact';
import { Spinner } from '@/components/common/Spinner';
import PartnerDeleteModal from '@/components/dashboard_components/manage_partner_dashboard/PartnerDeleteModal';
import PartnerMergeModal from '@/components/dashboard_components/manage_partner_dashboard/PartnerMergeModal';
import PartnerModal from '@/components/dashboard_components/manage_partner_dashboard/PartnerModal';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { SelectedPartnerContext } from '@/Contexts/SelectedPartnerContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { UsersContext } from '@/Contexts/UsersContext';
import usePartner from '@/hooks/usePartner';
import { ContactType, localizedTextEquals } from '@/models/Contact';
import { PartnerDashboardDetailsProps, PartnerDashboardProps } from '@/models/PartnerDashboard';
import { UserProps } from '@/models/User';

import PartnerImpersonationModal from './PartnerImpersonationModal';
import PartnerUpdateModal from './PartnerUpdateModal/PartnerUpdateModal';
import { ProgramNames } from '@/models/Program';

const PRIMARY_CONTACT_TOOLTIP =
  'Primary Contact: To update, impersonate the contact and edit via My Account. To change the primary contact, select the edit action.';

const sectionColumnSx = { display: 'flex', flexDirection: 'column', gap: '16px' };

function DetailsField({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px 10px',
        borderRadius: '8px',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      }}
    >
      <Typography variant='overline2' sx={{ letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography color='textSecondary' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.35 }}>
        {value ?? '---'}
      </Typography>
    </Box>
  );
}

function DetailsLongColumnField({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px',
        borderRadius: '8px',
        minHeight: '220px',
        height: '100%',
        minWidth: 0,
        overflow: 'hidden',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      }}
    >
      <Typography variant='overline2' sx={{ letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography
        color='textSecondary'
        sx={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.45,
          maxWidth: '100%',
          maxHeight: '220px',
          overflowY: 'auto',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          pr: '4px',
        }}
      >
        {value ?? '---'}
      </Typography>
    </Box>
  );
}

function PartnerDataGridDetails({
  partnerRow,
  updateRow,
}: {
  partnerRow: GridRowParams<PartnerDashboardProps>;
  updateRow: (updatedRow: PartnerDashboardProps) => void;
}) {
  const theme = useTheme();

  const { contactTypes } = useContext(ConfigContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);
  const { partnersAll } = useContext(SelectedPartnerContext);
  const { getPartnerDataGridDetails } = usePartner();
  const { usersAll } = useContext(UsersContext);

  const [selectedPartner, setSelectedPartner] = useState<PartnerDashboardDetailsProps | null>(null);

  const partnerId = partnerRow.row.id;

  const [modalPartnerRowParams, setModalPartnerRowParams] =
    useState<GridRowParams<PartnerDashboardDetailsProps>>();
  const [partnerModalOpen, setPartnerModalOpen] = useState<boolean>(false);
  const [partnerMergeModalOpen, setPartnerMergeModalOpen] = useState<boolean>(false);

  const [partnerDeleteModalOpen, setPartnerDeleteModalOpen] = useState<boolean>(false);
  const [partnerUpdateModalOpen, setPartnerUpdateModalOpen] = useState<boolean>(false);

  const [partnerImpersonationModalOpen, setPartnerImpersonationModalOpen] =
    useState<boolean>(false);

  const usersInPartnerGroup = useMemo(() => {
    if (usersAll == null || selectedPartner?.partnerGroup?.id == null) return null;

    return usersAll.filter(
      (user: UserProps) =>
        user.partnerGroupId === selectedPartner.partnerGroup?.id && !user.disabled
    );
  }, [selectedPartner?.partnerGroup?.id, usersAll]);

  const showImpersonationButton = useMemo(() => {
    return usersInPartnerGroup !== null && usersInPartnerGroup.length > 0;
  }, [usersInPartnerGroup]);

  const handlePartnerUpdateModalOpen = () => setPartnerUpdateModalOpen(true);
  const handlePartnerMergeModalOpen = () => setPartnerMergeModalOpen(true);
  const handlePartnerImpersonationModalOpen = () => setPartnerImpersonationModalOpen(true);
  const handleReassignModalOpen = () => {
    setModalPartnerRowParams(partnerRow);
    setPartnerModalOpen(true);
  };
  const handlePartnerDeleteModalOpen = () => setPartnerDeleteModalOpen(true);

  useEffect(() => {
    const fetchPartnerDetails = async (localPartnerId: number, programId: ProgramNames) => {
      const data = await getPartnerDataGridDetails(localPartnerId, programId);
      setSelectedPartner(data);
    };

    if (partnerId != null && selectedProgramId != null)
      void fetchPartnerDetails(partnerId, selectedProgramId);
  }, [getPartnerDataGridDetails, partnerId, selectedProgramId, partnerUpdateModalOpen]);

  const handlePartnerUpdate = useCallback(
    (partnerDetails: PartnerDashboardDetailsProps) => {
      if (partnerDetails != null) {
        setSelectedPartner(partnerDetails);
        updateRow(partnerDetails);
      }
    },
    [updateRow]
  );

  const shippingContactType = useMemo(
    () =>
      contactTypes.find((c: ContactType) =>
        localizedTextEquals(c.contactTypeName, 'Shipping Address')
      ),
    [contactTypes]
  );

  return selectedPartner != null ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        borderBottom: `1px solid ${theme.palette.sandShade2.main}`,
        height: 'calc(100% - 24px)',
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: theme.palette.sand.main,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '10px',
          borderRadius: '10px',
          backgroundColor: theme.palette.sandTint.main,
          borderBottom: `1px solid ${theme.palette.sandShade1.main}`,
        }}
      >
        <Button variant='secondary' size='small' onClick={handlePartnerUpdateModalOpen}>
          Edit
        </Button>
        <Button variant='secondary' size='small' onClick={handlePartnerMergeModalOpen}>
          Merge
        </Button>
        <Button variant='secondary' size='small' onClick={handleReassignModalOpen}>
          Re-Assign
        </Button>

        <Button variant='secondary' size='small' onClick={handlePartnerDeleteModalOpen}>
          Delete
        </Button>
        <Tooltip
          title={'No active users to impersonate'}
          disableHoverListener={showImpersonationButton}
          disableFocusListener={showImpersonationButton}
        >
          <span>
            <Button
              variant='secondary'
              size='small'
              onClick={handlePartnerImpersonationModalOpen}
              disabled={usersInPartnerGroup === null || usersInPartnerGroup.length === 0}
            >
              Impersonate
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            ...sectionColumnSx,
            minWidth: '220px',
            flex: '1 1 220px',
            padding: '14px',
            borderRadius: '10px',
            border: `1px solid ${theme.palette.sandShade2.main}`,
            backgroundColor: theme.palette.sandTint.main,
          }}
        >
          <Box display={'flex'} flexDirection={'row'} gap='8px' alignItems='center'>
            <Typography variant='h6' sx={{ lineHeight: 1.2 }}>
              Primary Contact
            </Typography>
            <Tooltip title={PRIMARY_CONTACT_TOOLTIP}>
              <InfoOutlinedIcon
                sx={{
                  color: theme.palette.info.main,
                  width: '20px',
                  height: '20px',
                }}
              />
            </Tooltip>
          </Box>

          {selectedPartner.primaryContact != null ? (
            <ReadOnlyContact
              contact={selectedPartner.primaryContact}
              programId={selectedProgramId}
            />
          ) : (
            <Typography color='textSecondary'>No Primary Contact</Typography>
          )}
        </Box>

        <Box
          sx={{
            ...sectionColumnSx,
            minWidth: '220px',
            flex: '1 1 220px',
            padding: '14px',
            borderRadius: '10px',
            border: `1px solid ${theme.palette.sandShade2.main}`,
            backgroundColor: theme.palette.sandTint.main,
          }}
        >
          <Typography variant='h6' sx={{ lineHeight: 1.2 }}>
            Default Shipping Contact
          </Typography>
          {selectedPartner.shippingContact != null ? (
            <ReadOnlyContact
              contact={selectedPartner.shippingContact}
              programId={selectedProgramId}
              contactType={shippingContactType}
            />
          ) : (
            <Typography color='textSecondary'>No Default Shipping Contact</Typography>
          )}
        </Box>

        <Box
          sx={{
            ...sectionColumnSx,
            minWidth: 0,
            flex: '2 1 640px',
            padding: '14px',
            borderRadius: '10px',
            border: `1px solid ${theme.palette.sandShade2.main}`,
            backgroundColor: theme.palette.sandTint.main,
          }}
        >
          <Typography variant='h6' sx={{ lineHeight: 1.2 }}>
            Additional Information
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(180px, 1fr) minmax(0, 2fr) minmax(0, 2fr)',
              },
              gap: '12px',
              alignItems: 'stretch',
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
              <DetailsField label='Current Year Status' value={selectedPartner.partnerStatus} />
              <DetailsField
                label='Last Certification Approval Year'
                value={selectedPartner.latestApprovalYear}
              />
              <DetailsField
                label='Additional Stickers'
                value={selectedPartner.additionalStickers}
              />
            </Box>

            <DetailsLongColumnField label='Notes' value={selectedPartner.notes} />
            <DetailsLongColumnField
              label='Special Instructions'
              value={selectedPartner.specialInstructions}
            />
          </Box>
        </Box>
      </Box>

      {partnerModalOpen && modalPartnerRowParams != null && selectedPartner != null && (
        <PartnerModal
          handleRowUpdate={updateRow}
          partnerModalOpen={partnerModalOpen}
          setPartnerModalOpen={setPartnerModalOpen}
          dashboardPartner={selectedPartner}
          theme={theme}
        />
      )}

      {partnerUpdateModalOpen && (
        <PartnerUpdateModal
          handleUpdate={handlePartnerUpdate}
          partnerUpdateModalOpen={partnerUpdateModalOpen}
          setPartnerUpdateModalOpen={setPartnerUpdateModalOpen}
          modalPartner={selectedPartner}
          theme={theme}
        />
      )}

      {partnerMergeModalOpen && selectedProgramId != null && (
        <PartnerMergeModal
          handleRowUpdate={updateRow}
          partnerInfo={selectedPartner}
          selectedProgramId={selectedProgramId}
          partnerMergeModalOpen={partnerMergeModalOpen}
          setPartnerMergeModalOpen={setPartnerMergeModalOpen}
          theme={theme}
          selectedPartners={partnersAll[selectedProgramId]}
        />
      )}

      {partnerDeleteModalOpen && (
        <PartnerDeleteModal
          modalPartner={selectedPartner}
          partnerDeleteModalOpen={partnerDeleteModalOpen}
          setPartnerDeleteModalOpen={setPartnerDeleteModalOpen}
          selectedProgramId={selectedProgramId}
        />
      )}

      {partnerImpersonationModalOpen && (
        <PartnerImpersonationModal
          modalPartner={selectedPartner}
          modalOpen={partnerImpersonationModalOpen}
          usersInPartnerGroup={usersInPartnerGroup}
          setModalOpen={setPartnerImpersonationModalOpen}
          theme={theme}
        />
      )}
    </Box>
  ) : (
    <Box display={'flex'} mt={5} alignContent={'center'} justifyContent={'center'}>
      <Spinner size={100} type='circular' />
    </Box>
  );
}

export default PartnerDataGridDetails;
